import { meetingOutcome, meetingStatus, type MoeteStatusOppforing, type MoeteUtfallOppforing } from '../../../theme/theme.ts';
import type { HypothesisConfig, HypothesisSummary } from '../api/useHypotheses.ts';
import type { DecisionMeeting, DecisionMeetingItem, DecisionMeetingRef, HypothesisScoreValues, MeetingOutcome, MeetingOutcomeKind } from '../api/useMoeter.ts';
import { fyllMal, navnAv, oppramsing } from './lesning.ts';
import { kortDato, tall } from './setning.ts';

/**
 * H3.2 — det møtets sider leser ut av dataene: navnet, utfallet som ord, tallene uten sum og hvem
 * som får svar. Ordene er DD-60s («Møter», «Agenda», «Skjermen i rommet», «Ikke i dag», «Bekreft»,
 * «Gi videre»); kontraktens enumverdi står aldri på en skjerm (DD-56).
 */

const MAANEDER_LANG = ['januar', 'februar', 'mars', 'april', 'mai', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'desember'];

/** «ÅÅÅÅ-MM-DD» → «20. oktober», uten tidssone: en `plainDate` er en dato, ikke et tidspunkt. */
export function langDato(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])}. ${MAANEDER_LANG[Number(m[2]) - 1] ?? ''}`;
}

/** Møtets navn: det som er satt, ellers «Møte 20. oktober» (P2). */
export const moeteNavn = (m: Pick<DecisionMeetingRef, 'title' | 'heldOn'>): string => m.title?.trim() || `Møte ${langDato(m.heldOn)}`;

/** Klokkeslettet i Europe/Oslo: «14:36». */
export const klokke = (t: string): string => new Date(t).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Oslo' });

export const MAKS_SITATER = 3;

export function moeteStatus(status: string): MoeteStatusOppforing {
  const o = meetingStatus[status];
  if (!o) throw new Error(`[innspel] Ukjent møtestatus «${status}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}

export function utfallOppforing(kind: MeetingOutcomeKind): MoeteUtfallOppforing {
  const o = meetingOutcome[kind];
  if (!o) throw new Error(`[innspel] Ukjent utfall «${kind}» — theme.ts og kontrakten er ute av synk.`);
  return o;
}

/** Utfallene i tastenes rekkefølge: V P A I. */
export const UTFALL: MeetingOutcomeKind[] = ['velg', 'parker', 'avvis', 'ikke_i_dag'];

/** Utfallet som gjelder: beslutningstakerens endring, ellers det registrerte. */
export const gjeldende = (i: DecisionMeetingItem): MeetingOutcome | undefined => i.decision ?? i.registered;

/** Radene som teller: ikke tatt ut, ikke gitt videre. */
export const aktiveRader = (m: DecisionMeeting): DecisionMeetingItem[] => [...m.items].sort((a, b) => a.position - b.position).filter((i) => i.state !== 'tatt_ut' && i.state !== 'gitt_videre');

export const alleRader = (m: DecisionMeeting): DecisionMeetingItem[] => [...m.items].sort((a, b) => a.position - b.position);

/** Avslagskodens etikett i rommets ordlyd — aldri koden. */
export const kodeNavn = (config: HypothesisConfig | undefined, kode: string | undefined): string | undefined => (kode ? (config?.rejectReasonWording.find((w) => w.code === kode)?.label ?? undefined) : undefined);

/** Utfallet som kort, sitert ord: «Valgt, Emil Strand eier» · «Parkert til 15. januar» · «Avvist, For stor» · «Ikke i dag». */
export function utfallKort(o: MeetingOutcome, config: HypothesisConfig | undefined): string {
  switch (o.outcome) {
    case 'velg':
      return o.owner ? `Valgt, ${navnAv(o.owner)} eier` : 'Valgt';
    case 'parker':
      return o.revisitAt ? `Parkert til ${langDato(o.revisitAt)}` : 'Parkert';
    case 'avvis': {
      const k = kodeNavn(config, o.rejectReason);
      return k ? `Avvist, ${k}` : 'Avvist';
    }
    default:
      return 'Ikke i dag';
  }
}

/** Pillens ord etter at utfallet er registrert: «Parkert til 15. januar», «Valgt», «Avvist», «Ikke i dag». */
export function utfallPille(o: MeetingOutcome): string {
  if (o.outcome === 'parker' && o.revisitAt) return `Parkert til ${langDato(o.revisitAt)}`;
  return utfallOppforing(o.outcome).etikett;
}

/**
 * Pillen på skjermen i rommet (P7): det som er notert, og at beslutningstakeren bekrefter etterpå.
 * Aldri tallene, aldri svaret.
 */
export function skjermUtfall(o: { outcome: MeetingOutcomeKind; owner?: { id: string; displayName?: string }; revisitAt?: string; rejectReason?: string; confirmed: boolean }, decider: { id: string; displayName?: string }, config: HypothesisConfig | undefined): string {
  const kode = kodeNavn(config, o.rejectReason);
  if (o.confirmed) {
    if (o.outcome === 'velg') return `Valgt${o.owner ? ` — ${navnAv(o.owner)} eier` : ''}`;
    if (o.outcome === 'parker') return `Parkert${o.revisitAt ? ` til ${langDato(o.revisitAt)}` : ''}`;
    if (o.outcome === 'avvis') return `Avvist${kode ? ` — ${kode}` : ''}`;
    return 'Ikke i dag';
  }
  const etterpaa = `${navnAv(decider)} bekrefter etterpå`;
  if (o.outcome === 'velg') return `Velges${o.owner ? ` — ${navnAv(o.owner)} eier` : ''} · ${etterpaa}`;
  if (o.outcome === 'parker') return `Parkeres${o.revisitAt ? ` til ${langDato(o.revisitAt)}` : ''} — ${etterpaa}`;
  if (o.outcome === 'avvis') return `Avvises${kode ? ` — ${kode}` : ''} · ${etterpaa}`;
  return `Ikke i dag — står i Vurderes`;
}

/** Impact som skalaen skriver den: 3 · 2 · 1 · ½ · ¼. */
export const impactTekst = (v: number): string => (v === 0.5 ? '½' : v === 0.25 ? '¼' : tall(v));

/**
 * «Impact 2 · Confidence 80 % · Effort 3 ukesverk» — de tre som er satt, aldri en sum (låst:
 * «Impact, Confidence og Effort er alltid et menneske»). `kort` gir «3 uv» på 400 px.
 */
export function tallTekst(s: HypothesisScoreValues, kort = false): string | null {
  const deler = [s.impact !== undefined ? `Impact ${impactTekst(s.impact)}` : null, s.confidence !== undefined ? `Confidence ${s.confidence} %` : null, s.effort !== undefined ? `Effort ${tall(s.effort)} ${kort ? 'uv' : 'ukesverk'}` : null].filter((d): d is string => d !== null);
  return deler.length ? deler.join(' · ') : null;
}

const ORD = ['null', 'én', 'to', 'tre', 'fire', 'fem', 'seks', 'sju', 'åtte', 'ni', 'ti'];
/** «fem» for 5, tallet over ti. Tallet i knappen og i setningen («Bekreft alle fem»). */
export const somOrd = (n: number): string => ORD[n] ?? String(n);

/** Innsenderens navn på en rad — og medinnsenderne. */
export const innsendere = (h: HypothesisSummary): string[] => [...new Set([navnAv(h.submittedBy), ...(h.coSubmitters ?? []).map((c) => navnAv(c.admin))])];

/**
 * Hvem som får svar når møtet bekreftes, og hvem som venter på en begrunnelse (kjerne 5.3): velg og
 * parker får svar; avvis bare når teksten finnes; «Ikke i dag» får ingenting.
 */
export function svarVed(rader: DecisionMeetingItem[]): { faar: string[]; venter: string[] } {
  const faar: string[] = [];
  const venter: string[] = [];
  for (const i of rader) {
    const o = gjeldende(i);
    if (!o || o.outcome === 'ikke_i_dag') continue;
    if (o.outcome === 'avvis' && !o.response) venter.push(...innsendere(i.hypothesis));
    else faar.push(...innsendere(i.hypothesis));
  }
  return { faar: [...new Set(faar)], venter: [...new Set(venter)] };
}

export const navneliste = (navn: string[]): string => oppramsing(navn);

/**
 * Svaret malen gir for utfallet (`HypothesisTemplateUse` valgt/parkert), med plassholderne fylt.
 * Avvis har kodens mal, som må skrives om (DD-09); den fylles først når koden er valgt.
 */
export function malFor(config: HypothesisConfig | undefined, outcome: MeetingOutcomeKind, verdier: { eier?: string; dato?: string; navn?: string; kode?: string }): { text: string; templateId?: string } | null {
  if (outcome === 'avvis') {
    const k = config?.rejectReasonWording.find((w) => w.code === verdier.kode);
    return k?.template ? { text: fyllMal(k.template, { navn: verdier.navn }) } : null;
  }
  const bruk = outcome === 'velg' ? 'valgt' : outcome === 'parker' ? 'parkert' : null;
  const mal = bruk ? config?.responseTemplates.find((t) => t.use === bruk) : undefined;
  if (!mal) return null;
  return { text: fyllMal(mal.text, { eier: verdier.eier, dato: verdier.dato, navn: verdier.navn }), templateId: mal.id };
}

/** «12. nov» for fristen på en rad, fra `deadlineAt`. */
export const fristKort = (h: HypothesisSummary): string | null => (h.deadlineAt ? kortDato(new Date(h.deadlineAt).toISOString().slice(0, 10)) : null);
